import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, PencilSimple, Trash, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BLANK = {
  name: "", franchisee_id: "", address: "", latitude: "", longitude: "",
  phone: "", opens_at: "09:00", closes_at: "22:00", tax_rate: 0, active: true,
};

export default function Outlets() {
  const [items, setItems] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [o, f] = await Promise.all([
      api.get("/outlets"),
      api.get("/franchisees"),
    ]);
    setItems(o.data);
    setFranchisees(f.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) return toast.error("Name required");
    const payload = {
      ...editing,
      latitude: editing.latitude === "" ? null : parseFloat(editing.latitude),
      longitude: editing.longitude === "" ? null : parseFloat(editing.longitude),
      tax_rate: parseFloat(editing.tax_rate) || 0,
    };
    delete payload.id;
    delete payload.created_at;
    try {
      if (editing.id) {
        await api.patch(`/outlets/${editing.id}`, payload);
      } else {
        await api.post("/outlets", payload);
      }
      setEditing(null);
      load();
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this outlet?")) return;
    try {
      await api.delete(`/outlets/${id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const frName = (id) => franchisees.find((f) => f.id === id)?.name || "—";

  return (
    <div className="h-screen flex flex-col">
      <div className="p-6 bg-white border-b border-boba-stoneLine flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Outlets</h1>
          <p className="text-sm text-boba-inkSoft mt-1">Physical Hizli stores</p>
        </div>
        <button onClick={() => setEditing({ ...BLANK, franchisee_id: franchisees[0]?.id || "" })} className="h-11 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium flex items-center gap-2" data-testid="add-outlet-btn">
          <Plus size={18} weight="bold" /> Add Outlet
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="outlets-grid">
          {items.map((o) => (
            <div key={o.id} className="bg-white border border-boba-stoneLine rounded-md p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md bg-boba-matcha/10 text-boba-matcha flex items-center justify-center">
                    <Storefront size={20} weight="duotone" />
                  </div>
                  <div>
                    <div className="font-display text-lg font-bold leading-tight">{o.name}</div>
                    <div className="text-xs text-boba-inkSoft mt-1">{frName(o.franchisee_id)}</div>
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-mono px-2 py-1 rounded-full ${o.active ? "bg-boba-matcha/10 text-boba-matcha" : "bg-stone-100 text-stone-500"}`}>
                  {o.active ? "Open" : "Closed"}
                </span>
              </div>
              <div className="mt-4 space-y-1.5 text-sm">
                {o.address && <div className="text-boba-inkSoft">📍 {o.address}</div>}
                {o.phone && <div className="font-mono text-boba-inkSoft">☎ {o.phone}</div>}
                <div className="font-mono text-xs text-boba-inkSoft">🕒 {o.opens_at} – {o.closes_at}</div>
                {o.tax_rate > 0 && <div className="font-mono text-xs text-boba-inkSoft">Tax: {Math.round(o.tax_rate * 100)}%</div>}
                {(o.latitude && o.longitude) && (
                  <div className="font-mono text-[10px] text-boba-inkSoft">GPS {o.latitude.toFixed(4)}, {o.longitude.toFixed(4)}</div>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-boba-stoneLine flex justify-end gap-1">
                <button onClick={() => setEditing(o)} className="text-xs px-3 py-1.5 hover:bg-stone-100 rounded inline-flex items-center gap-1" data-testid={`edit-outlet-${o.id}`}>
                  <PencilSimple size={14} /> Edit
                </button>
                <button onClick={() => del(o.id)} className="text-xs px-3 py-1.5 hover:bg-red-50 text-boba-danger rounded inline-flex items-center gap-1">
                  <Trash size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="col-span-full text-center py-16 text-boba-inkSoft">No outlets yet</div>
          )}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl" data-testid="outlet-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing?.id ? "Edit Outlet" : "New Outlet"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <Field label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="ol-name" />
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Franchisee *</label>
                <select value={editing.franchisee_id || ""} onChange={(e) => setEditing({ ...editing, franchisee_id: e.target.value })} className="w-full mt-1 h-10 px-2 border border-boba-stoneLine rounded-md bg-white" data-testid="ol-franchisee">
                  <option value="">— Select —</option>
                  {franchisees.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <Field label="Address" value={editing.address} onChange={(v) => setEditing({ ...editing, address: v })} testId="ol-address" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Latitude" type="number" step="0.000001" value={editing.latitude ?? ""} onChange={(v) => setEditing({ ...editing, latitude: v })} testId="ol-lat" />
                <Field label="Longitude" type="number" step="0.000001" value={editing.longitude ?? ""} onChange={(v) => setEditing({ ...editing, longitude: v })} testId="ol-lng" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Phone" value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} testId="ol-phone" />
                <Field label="Opens" type="time" value={editing.opens_at} onChange={(v) => setEditing({ ...editing, opens_at: v })} testId="ol-opens" />
                <Field label="Closes" type="time" value={editing.closes_at} onChange={(v) => setEditing({ ...editing, closes_at: v })} testId="ol-closes" />
              </div>
              <Field label="Tax rate (0.05 = 5%)" type="number" step="0.001" value={editing.tax_rate} onChange={(v) => setEditing({ ...editing, tax_rate: v })} testId="ol-tax" />
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} id="ol-active" />
                <label htmlFor="ol-active" className="text-sm">Active</label>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-boba-stoneLine">
                <button onClick={() => setEditing(null)} className="h-10 px-4 border border-boba-stoneLine rounded-md hover:bg-stone-50">Cancel</button>
                <button onClick={save} className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium" data-testid="ol-save">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">{label}</label>
      <input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md"
        data-testid={testId}
      />
    </div>
  );
}
